import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Truck, Users, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTukTuks = () => {
  const [tuktuks, setTuktuks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', driver_name: '', capacity: 4, description: '' });
  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTukTuks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/tuktuks`, { headers });
      setTuktuks(res.data);
    } catch (err) {
      toast.error('Error al cargar tuk-tuks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTukTuks(); }, [fetchTukTuks]);

  const handleSeed = async () => {
    try {
      const res = await axios.post(`${API}/admin/tuktuks/seed`, {}, { headers });
      toast.success(res.data.message);
      fetchTukTuks();
    } catch (err) {
      toast.error('Error al crear datos iniciales');
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', driver_name: '', capacity: 4, description: '' });
    setShowForm(true);
  };

  const openEdit = (tt) => {
    setEditing(tt);
    setForm({ name: tt.name, driver_name: tt.driver_name || '', capacity: tt.capacity, description: tt.description || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/tuktuks/${editing.id}`, form, { headers });
        toast.success('Tuk-tuk actualizado');
      } else {
        await axios.post(`${API}/admin/tuktuks`, form, { headers });
        toast.success('Tuk-tuk creado');
      }
      setShowForm(false);
      fetchTukTuks();
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tuk-tuk?')) return;
    try {
      await axios.delete(`${API}/admin/tuktuks/${id}`, { headers });
      toast.success('Tuk-tuk eliminado');
      fetchTukTuks();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="admin-tuktuks-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1917]">Gestión de Tuk-Tuks</h1>
          <p className="text-sm text-[#57534E] mt-1">Administra tu flota de vehículos</p>
        </div>
        <div className="flex gap-2">
          {tuktuks.length === 0 && (
            <Button variant="outline" onClick={handleSeed} data-testid="seed-tuktuks-btn">
              Datos iniciales
            </Button>
          )}
          <Button onClick={openNew} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="add-tuktuk-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuevo Tuk-Tuk
          </Button>
        </div>
      </div>

      {tuktuks.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-[#E7E5E4]">
          <Truck className="h-12 w-12 mx-auto text-[#D6D3D1] mb-4" />
          <h3 className="text-lg font-semibold text-[#1C1917] mb-2">No hay tuk-tuks</h3>
          <p className="text-sm text-[#57534E] mb-4">Pulsa &ldquo;Datos iniciales&rdquo; para crear el primer tuk-tuk</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {tuktuks.map((tt) => (
            <div
              key={tt.id}
              className="bg-white rounded-xl border border-[#E7E5E4] p-5 flex items-center justify-between hover:shadow-sm transition-shadow"
              data-testid={`tuktuk-card-${tt.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                  <Truck className="h-6 w-6 text-[#C2410C]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1C1917]">{tt.name}</h3>
                  {tt.driver_name && (
                    <p className="text-sm text-[#57534E]">Conductor: {tt.driver_name}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs bg-[#FDFBF7] border border-[#E7E5E4] rounded-full px-2 py-0.5 flex items-center gap-1">
                      <Users className="h-3 w-3" /> {tt.capacity} plazas
                    </span>
                    {tt.description && (
                      <span className="text-xs text-[#78716C]">{tt.description}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openEdit(tt)} data-testid={`edit-tuktuk-${tt.id}`}>
                  <Pencil className="h-4 w-4 text-[#57534E]" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(tt.id)} data-testid={`delete-tuktuk-${tt.id}`}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info about availability */}
      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold mb-1">Sistema de disponibilidad activo</p>
          <p>Los clientes verán los tuk-tuks disponibles al seleccionar fecha y hora en la reserva. Los tuk-tuks ya reservados aparecerán deshabilitados con &ldquo;Ya reservado&rdquo;.</p>
        </div>
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md" data-testid="tuktuk-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Tuk-Tuk' : 'Nuevo Tuk-Tuk'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Ej: Carlos Casal"
                data-testid="tuktuk-name-input"
              />
            </div>
            <div>
              <Label>Conductor</Label>
              <Input
                value={form.driver_name}
                onChange={(e) => setForm(p => ({ ...p, driver_name: e.target.value }))}
                placeholder="Nombre del conductor"
                data-testid="tuktuk-driver-input"
              />
            </div>
            <div>
              <Label>Capacidad (pasajeros)</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={form.capacity}
                onChange={(e) => setForm(p => ({ ...p, capacity: parseInt(e.target.value) || 4 }))}
                data-testid="tuktuk-capacity-input"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Detalles adicionales..."
                rows={2}
                data-testid="tuktuk-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="tuktuk-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editing ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTukTuks;
